import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import TagManager from "react-gtm-module";

import { DISEASE_TYPES, WORKFLOWS } from "helpers/apis";
import useAxios from "helpers/axios";
import { IDiseaseType } from "types/diseaseType";
import { IWorkflow } from "types/workflow";

export enum ProductLevel {
  MVP = "MVP",
  R1 = "R1",
}

export enum ToastTypes {
  SUCCESS = "success",
  ERROR = "error",
}

interface IToast {
  message: string;
  type?: ToastTypes;
  duration?: number;
}

interface IAppContextType {
  diseaseTypes: IDiseaseType[];
  loading: boolean;
  toastMessage: IToast | null;
  workflows: IWorkflow[];
  getDiseaseTypes: Function;
  getWorkflow: Function;
  setToastMessage: (toastMessage: IToast | null) => void;
  productLevel: ProductLevel;
}

export const AppContext = createContext<IAppContextType>({} as IAppContextType);

export function AppProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const history = useHistory();
  const { pathname } = useLocation();
  const [toastMessage, setToastMessage] = useState<IToast | null>(null);
  const [workflows, setWorkflows] = useState<IWorkflow[]>([]);
  const [diseaseTypes, setDiseaseTypes] = useState<IDiseaseType[]>([]);
  const [productLevel] = useState<ProductLevel>(ProductLevel.MVP); // May be loaded in the future

  useEffect(() => {
    // GA
    const unlisten = history.listen((location) => {
      TagManager.dataLayer({
        dataLayer: {
          event: "page_view",
          pagePath: location.pathname,
        },
      });
      // Scroll to top
      window.scrollTo(0, 0);
    });

    return unlisten;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Remove styles attributes on route change because of popover scrolling issue
    document.body.removeAttribute("style");
  }, [pathname]);

  // Load disease types
  const { loading: diseaseTypeLoading } = useAxios({
    config: { url: DISEASE_TYPES },
    successCallback: ({ response }: any) => {
      if (response !== null) {
        setDiseaseTypes(response.data);
      } else {
        setDiseaseTypes([]);
      }
    },
  });
  // Load workflows
  const { loading: workflowLoading } = useAxios({
    config: { url: WORKFLOWS },
    successCallback: ({ response }: any) => {
      if (response !== null) {
        setWorkflows(response.data);
      } else {
        setWorkflows([]);
      }
    },
  });

  const memoedValue = useMemo(
    () => ({
      diseaseTypes,
      loading: diseaseTypeLoading || workflowLoading,
      toastMessage,
      workflows,
      getDiseaseTypes: (ids: number[] | number) => {
        if (!Array.isArray(ids)) {
          ids = [ids];
        }
        return diseaseTypes.filter((diseaseType: IDiseaseType) =>
          (ids as number[]).includes(diseaseType.id)
        );
      },
      getWorkflow: (id: number) => {
        return workflows.find((workflow: IWorkflow) => workflow.id === id);
      },
      productLevel,
      setToastMessage,
    }),
    [
      diseaseTypes,
      diseaseTypeLoading,
      toastMessage,
      workflows,
      workflowLoading,
      productLevel,
    ]
  );

  return (
    <AppContext.Provider value={memoedValue}>{children}</AppContext.Provider>
  );
}

export default function useApp() {
  return useContext(AppContext);
}
